import {
  DrilldownTableConfigContextProviderProps,
  DrilldownTableExtraColumn,
} from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  DrilldownTablePanelData,
} from 'features/drilldown/components/table/DrilldownTable'
import { parseZonesFromTicket } from 'features/drilldown/panels/impl/niiLibraResultsByZoneTable/expandLibraNiiTicketZoneRows'
import { GeckoEntry, RolodexEntryType } from 'types'
import { displayNameFromEmail } from 'utils/stringHelpers'

import {
  findTicket,
  NII_LIBRA_ELIGIBILITY_COLUMN,
  NII_LIBRA_EXEC_SUMMARY_COLUMN,
  NII_LIBRA_RECOMMENDATIONS_COLUMN,
  NII_LIBRA_TICKET_STATUS_COLUMN,
  readStringDataField,
  renderPlainTextColumn,
} from './NiiLibraSharedTableColumns'

type NiiLibraResultsPanelData = DrilldownTablePanelData

const TICKET_COLUMNS: ReadonlyArray<DrilldownTableColumnDef> = [
  { field: 'unit.id', title: 'FLO' },
  { field: 'unit.description', title: 'Asset Description' },
  {
    field: 'libra-ticket.fieldValues.insulation.value',
    title: 'Is Insulated',
  },
]

function readNumberDataField(data: unknown, field: string): number | undefined {
  if (!data || typeof data !== 'object') return undefined
  const val = (data as Record<string, unknown>)[field]
  return typeof val === 'number' && Number.isFinite(val) ? val : undefined
}

function readZoneCountDisplay(entries: ReadonlyArray<GeckoEntry>): string {
  const ticket = findTicket(entries)
  if (!ticket) return ''
  const zones = parseZonesFromTicket(ticket)
  return zones.length > 0 ? String(zones.length) : ''
}

function readVersionDisplay(entries: ReadonlyArray<GeckoEntry>): string {
  const ticket = findTicket(entries)
  const version = readNumberDataField(ticket?.data, 'version')
  return version !== undefined ? `v${version}` : ''
}

function readCompletedByRaw(entries: ReadonlyArray<GeckoEntry>): string {
  const ticket = findTicket(entries)
  return (
    readStringDataField(ticket?.data, 'terminalTransitionBy') ||
    readStringDataField(ticket?.data, 'completedBy')
  )
}

function readCompletedByDisplay(entries: ReadonlyArray<GeckoEntry>): string {
  const raw = readCompletedByRaw(entries)
  return raw ? displayNameFromEmail(raw) : ''
}

const ANALYSIS_EXTRA_COLUMNS: ReadonlyArray<DrilldownTableExtraColumn> = [
  NII_LIBRA_TICKET_STATUS_COLUMN,
  {
    id: 'nii_libra_zone_count',
    name: 'Zones',
    linked_types: [RolodexEntryType.LIBRA_TICKET],
    width: '5rem',
    render: renderPlainTextColumn(({ entries }) =>
      readZoneCountDisplay(entries)
    ),
    exportValue: ({ entries }) => readZoneCountDisplay(entries),
  },
  NII_LIBRA_ELIGIBILITY_COLUMN,
  {
    id: 'nii_libra_version',
    name: 'Version',
    linked_types: [RolodexEntryType.LIBRA_TICKET],
    width: '5rem',
    render: renderPlainTextColumn(({ entries }) => readVersionDisplay(entries)),
    exportValue: ({ entries }) => readVersionDisplay(entries),
  },
  {
    id: 'nii_libra_completed_by',
    name: 'Completed By',
    linked_types: [RolodexEntryType.LIBRA_TICKET],
    width: '10rem',
    render: renderPlainTextColumn(({ entries }) =>
      readCompletedByDisplay(entries)
    ),
    exportValue: ({ entries }) => readCompletedByDisplay(entries),
  },
  NII_LIBRA_EXEC_SUMMARY_COLUMN,
  NII_LIBRA_RECOMMENDATIONS_COLUMN,
]

export const DEFAULT_NII_LIBRA_RESULTS_TABLE_PANEL_DATA: NiiLibraResultsPanelData =
  {
    ...DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
    title: 'NII Analysis Results',
    groupByType: 'libra-ticket',
    columns: [...TICKET_COLUMNS],
    enabledExtraColumnIds: ANALYSIS_EXTRA_COLUMNS.map((c) => c.id),
  }

export const NII_LIBRA_RESULTS_TABLE_CONFIG_PROPS: Omit<
  DrilldownTableConfigContextProviderProps,
  'children'
> = {
  allowSetColumns: false,
  allowSetGroupByType: false,
  extraColumns: ANALYSIS_EXTRA_COLUMNS,
  initialSortingOpts: [{ order: 'descend', columnKey: 'unit.id' }],
  paginatedTableOptions: {
    defaultPageSize: 30,
    tableRowKey: (row) => row.id,
  },
}
